import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { ModalWrapper } from "@msuite/ui/modal";
import { type CellContext, RowActionsWrapper } from "@msuite/ui/table";
import { Tooltip } from "@msuite/ui/tooltip";
import type { IncomingInvoiceDeliveryNoteListItem } from "@msuite/utils/types";
import React from "react";
import { IncomingInvoiceDocumentPreviewButton } from "@/components/incoming-invoices";
import { useCitadel } from "@/hooks/use-citadel";
import { LinkDeliveryNoteInvoiceModal } from "@/modals/incoming-invoices/link-delivery-note-invoice-modal";

/**
 * The delivery note's PDF in the viewer, and its link to the invoice that bills it — set by hand
 * for one still waiting, changed for one linked wrongly. A linked delivery note also opens its
 * invoice.
 */
export function RowActions({ cell }: CellContext<IncomingInvoiceDeliveryNoteListItem, "actions">) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const { citadel } = useCitadel();
	const linkModal = useDisclosure();

	/** Computed */
	const deliveryNote = cell.row.original;
	const { invoice, fileId } = deliveryNote;
	const mayLink = citadel?.can("update", "IncomingInvoice") === true;

	/** Render */
	return (
		<React.Fragment>
			<RowActionsWrapper to={invoice ? `/controlling/incoming-invoices/${invoice.id}` : null}>
				{fileId && <IncomingInvoiceDocumentPreviewButton fileId={fileId} />}
				{mayLink && (
					<Tooltip label={t("incoming_invoices.delivery_notes.link_invoice")}>
						<Button
							size="xxs"
							variant="ghost"
							icon={<Icon name="Link2" />}
							onClick={linkModal.onOpen}
							data-cy="link-incoming-invoice-delivery-note-button"
						/>
					</Tooltip>
				)}
			</RowActionsWrapper>
			<ModalWrapper>
				{linkModal.isOpen && (
					<LinkDeliveryNoteInvoiceModal
						isOpen={linkModal.isOpen}
						onClose={linkModal.onClose}
						deliveryNote={deliveryNote}
					/>
				)}
			</ModalWrapper>
		</React.Fragment>
	);
}
